<template>
  <div class="ncrm-icon-grid-picker">
    <v-text-field
      :model-value="selectedItem.label"
      :label="label"
      :prepend-inner-icon="selectedItem.icon"
      append-inner-icon="mdi-chevron-down"
      readonly
      hide-details
      @click="openDialog"
      @keydown.enter.prevent="openDialog"
      @keydown.space.prevent="openDialog"
    />

    <v-dialog v-model="dialog" max-width="760">
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between ga-2">
          <span>{{ dialogTitle || label }}</span>
          <v-btn icon="mdi-close" variant="text" @click="closeDialog" />
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-text-field
            v-model="search"
            :label="searchLabel"
            prepend-inner-icon="mdi-magnify"
            clearable
            hide-details
          />

          <div class="ncrm-icon-grid-picker__current mt-4">
            <div class="ncrm-icon-grid-picker__current-icon">
              <v-icon size="22" color="primary">{{ selectedItem.icon }}</v-icon>
            </div>
            <div>
              <div class="text-body-2 ncrm-text-strong">{{ selectedItem.label }}</div>
              <div class="text-caption text-medium-emphasis">{{ selectedItem.value || "-" }}</div>
            </div>
          </div>

          <div v-if="filteredItems.length" class="ncrm-icon-grid-picker__grid mt-4">
            <button
              v-for="item in filteredItems"
              :key="item.value || '__empty__'"
              type="button"
              class="ncrm-icon-grid-picker__tile"
              :class="{ 'ncrm-icon-grid-picker__tile--selected': isSelected(item.value) }"
              @click="selectValue(item.value)"
            >
              <v-icon size="22" color="primary">{{ item.icon }}</v-icon>
              <span class="ncrm-icon-grid-picker__tile-label">{{ item.label }}</span>
            </button>
          </div>

          <v-alert v-else type="info" class="mt-4">
            {{ emptyText }}
          </v-alert>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
const normalizeText = (value) => String(value ?? "").trim().toLowerCase();

export default {
  name: "NcrmIconGridPicker",
  props: {
    modelValue: {
      type: String,
      default: "",
    },
    items: {
      type: Array,
      default: () => [],
    },
    label: {
      type: String,
      default: "",
    },
    dialogTitle: {
      type: String,
      default: "",
    },
    searchLabel: {
      type: String,
      default: "",
    },
    emptyText: {
      type: String,
      default: "",
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      dialog: false,
      search: "",
    };
  },
  computed: {
    normalizedItems() {
      return (Array.isArray(this.items) ? this.items : []).map((item) => ({
        value: String(item?.value ?? ""),
        label: String(item?.label || item?.value || "-"),
        icon: String(item?.icon || "mdi-shape-outline"),
      }));
    },
    selectedItem() {
      return (
        this.normalizedItems.find((item) => String(item.value) === String(this.modelValue ?? "")) ||
        this.normalizedItems[0] || {
          value: "",
          label: "-",
          icon: "mdi-shape-outline",
        }
      );
    },
    filteredItems() {
      const normalizedSearch = normalizeText(this.search);
      if (!normalizedSearch) return this.normalizedItems;
      return this.normalizedItems.filter((item) => {
        const haystack = `${item.label} ${item.value}`.toLowerCase();
        return haystack.includes(normalizedSearch);
      });
    },
  },
  methods: {
    openDialog() {
      this.search = "";
      this.dialog = true;
    },
    closeDialog() {
      this.dialog = false;
      this.search = "";
    },
    selectValue(value) {
      this.$emit("update:modelValue", String(value ?? ""));
      this.closeDialog();
    },
    isSelected(value) {
      return String(value ?? "") === String(this.modelValue ?? "");
    },
  },
};
</script>

<style scoped>
.ncrm-icon-grid-picker__current {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ncrm-icon-grid-picker__current-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 1px solid var(--ncrm-color-border-subtle);
  border-radius: var(--app-radius-base);
  background: var(--ncrm-color-surface-muted);
}

.ncrm-icon-grid-picker__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(112px, 1fr));
  gap: 12px;
}

.ncrm-icon-grid-picker__tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 104px;
  padding: 12px;
  border: 1px solid var(--ncrm-color-border-default);
  border-radius: var(--app-radius-base);
  background: rgb(var(--v-theme-surface));
  cursor: pointer;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.ncrm-icon-grid-picker__tile:hover,
.ncrm-icon-grid-picker__tile:focus-visible {
  border-color: var(--ncrm-color-primary-border);
  box-shadow: 0 8px 20px rgba(var(--v-theme-on-surface), 0.08);
  transform: translateY(-1px);
  outline: none;
}

.ncrm-icon-grid-picker__tile--selected {
  border-color: var(--ncrm-color-primary-border);
  background: var(--ncrm-color-hover-soft);
}

.ncrm-icon-grid-picker__tile-label {
  font-size: 0.82rem;
  line-height: 1.25;
  text-align: center;
  color: var(--ncrm-color-text-strong);
}
</style>
